/**
 * Vendor selection and access-code rules for the Self-Serve class form (ELS-638 / ELS-640).
 *
 * Extracted from `add.self.serve.class.tsx` so the decisions with money attached — which
 * vendors may be combined, and whose codes get revoked on save — are unit-testable without
 * mounting the form.
 *
 * @see docs/edsims/modules/21-self-serve-enrollment.md
 */

export const STUDENT_PAY = "STUDENT_PAY";
export const VITAL_SOURCE = "VITAL_SOURCE";
export const FOLLETT = "FOLLETT";

export const VENDOR_OPTIONS = [
  { value: STUDENT_PAY, label: "Student Pay" },
  { value: VITAL_SOURCE, label: "VitalSource" },
  { value: FOLLETT, label: "Follett" },
];

/** Codes may only ever be pre-generated for these; Student Pay issues at payment time. */
export const BOOKSTORE_VENDORS = [VITAL_SOURCE, FOLLETT];

export const MIN_GENERATE_QUANTITY = 1;
export const MAX_GENERATE_QUANTITY = 1000;

export type VendorInventory = {
  available: number;
  redeemed: number;
  expired: number;
  revoked: number;
};

export type VendorInventoryMap = Record<string, VendorInventory>;

export type VendorOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

export const EMPTY_INVENTORY: VendorInventory = {
  available: 0,
  redeemed: 0,
  expired: 0,
  revoked: 0,
};

export const isBookstoreVendor = (vendorKey: string): boolean =>
  BOOKSTORE_VENDORS.indexOf(vendorKey) >= 0;

/**
 * Purchase Type pattern: mutually exclusive sides are greyed out, not warned after the fact.
 * Student Pay selected → bookstore options disabled. Bookstore selected → Student Pay disabled.
 * VitalSource and Follett stay combinable.
 */
export const vendorOptionsForSelection = (
  selectedCodes: string[] | undefined | null
): VendorOption[] => {
  const selected = selectedCodes || [];
  const hasStudentPay = selected.indexOf(STUDENT_PAY) >= 0;
  const hasBookstore = selected.some(isBookstoreVendor);
  return VENDOR_OPTIONS.map((option) => {
    const disableBookstore = hasStudentPay && isBookstoreVendor(option.value);
    const disableStudentPay = hasBookstore && option.value === STUDENT_PAY;
    return {
      ...option,
      isDisabled: disableBookstore || disableStudentPay,
    };
  });
};

export const vendorLabel = (vendorKey: string): string =>
  VENDOR_OPTIONS.find((v) => v.value === vendorKey)?.label || vendorKey;

export const vendorInventory = (
  byVendor: VendorInventoryMap | undefined | null,
  vendorKey: string
): VendorInventory => byVendor?.[vendorKey] || EMPTY_INVENTORY;

/** Saved vendors that can be used for Generate — Student Pay is excluded by design. */
export const savedBookstoreVendorKeys = (
  savedVendors: string[] | undefined | null
): string[] => (savedVendors || []).filter(isBookstoreVendor);

/** Bookstore vendors picked in the form but not yet persisted, so they hold no inventory. */
export const unsavedBookstoreVendorKeys = (
  savedVendors: string[] | undefined | null,
  selectedCodes: string[] | undefined | null
): string[] => {
  const saved = savedBookstoreVendorKeys(savedVendors);
  return (selectedCodes || []).filter(
    (code) => isBookstoreVendor(code) && saved.indexOf(code) < 0
  );
};

/**
 * Codes a vendor removal would revoke: everything except what is already revoked.
 *
 * Removing a vendor withdraws the channel, so redeemed and expired codes go with it — not
 * just unsold inventory.
 */
export const revocableCount = (inventory: VendorInventory): number =>
  inventory.available + inventory.redeemed + inventory.expired;

/** Total codes across the given vendors that students have already redeemed. */
export const redeemedCount = (
  vendorKeys: string[],
  byVendor: VendorInventoryMap | undefined | null
): number =>
  vendorKeys.reduce(
    (total, key) => total + vendorInventory(byVendor, key).redeemed,
    0
  );

/**
 * e.g. `"Follett — 240 access codes (12 already redeemed)"`, comma-joined across vendors.
 *
 * The redeemed count is called out separately because it is the part that costs a student
 * their access, and it is invisible in the plain total.
 */
export const revokeSummary = (
  vendorKeys: string[],
  byVendor: VendorInventoryMap | undefined | null
): string =>
  vendorKeys
    .map((key) => {
      const inventory = vendorInventory(byVendor, key);
      const redeemed = inventory.redeemed;
      return `${vendorLabel(key)} — ${revocableCount(inventory)} access code${
        revocableCount(inventory) === 1 ? "" : "s"
      }${redeemed > 0 ? ` (${redeemed} already redeemed)` : ""}`;
    })
    .join(", ");

/**
 * Saved vendors dropped from the selection that still hold codes worth revoking.
 *
 * Gated on both conditions on purpose: a vendor added and removed within the same session
 * has no inventory yet, so save has nothing to revoke for it.
 */
export const pendingRevokeVendors = (
  savedVendors: string[] | undefined | null,
  selectedCodes: string[] | undefined | null,
  byVendor: VendorInventoryMap | undefined | null
): string[] => {
  const selected = selectedCodes || [];
  return (savedVendors || []).filter(
    (code) =>
      selected.indexOf(code) < 0 &&
      revocableCount(vendorInventory(byVendor, code)) > 0
  );
};

/**
 * What the admin is told when a vendor is dropped.
 *
 * Names the vendor(s); counts stay off this copy.
 */
export const revokeWarning = (
  vendorKeys?: string[],
  _byVendor?: VendorInventoryMap | undefined | null
): string => {
  const names = (vendorKeys || [])
    .map((key) => vendorLabel(key))
    .filter(Boolean);
  if (names.length === 0) {
    return "All the generated access codes will be marked revoked.";
  }
  const list =
    names.length === 1
      ? names[0]
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  return `All the generated access codes for ${list} will be marked revoked.`;
};

export type BlockedSelection = {
  blocked: true;
  message: string;
};

export type LandingOptions = {
  landingOptionPay: boolean;
  landingOptionEnterCode: boolean;
  landingOptionForgotCode: boolean;
};

export type AcceptedSelection = {
  blocked: false;
  pendingRevokeVendors: string[];
  warning: string;
  landingOptions: LandingOptions;
};

export type VendorSelectionResult = BlockedSelection | AcceptedSelection;

const PAY_DEFAULTS: LandingOptions = {
  landingOptionPay: true,
  landingOptionEnterCode: true,
  landingOptionForgotCode: true,
};

const BOOKSTORE_DEFAULTS: LandingOptions = {
  landingOptionPay: false,
  landingOptionEnterCode: true,
  landingOptionForgotCode: true,
};

/**
 * The vendor **seeds** the landing options; it does not own them.
 *
 * Switching between Student Pay and bookstore reseeds, because the previous answers describe a
 * different kind of enrolment. Any other vendor edit — adding Follett alongside VitalSource,
 * clearing the field — leaves the admin's own choices intact, since they still mean the same
 * thing. "Register and pay" is the one exception: it cannot be on without Student Pay, so it is
 * forced off rather than preserved.
 */
export const reconcileLandingOptions = (
  current: LandingOptions,
  previousCodes: string[] | undefined | null,
  nextCodes: string[] | undefined | null
): LandingOptions => {
  const wasPay = (previousCodes || []).indexOf(STUDENT_PAY) >= 0;
  const nowPay = (nextCodes || []).indexOf(STUDENT_PAY) >= 0;

  if (wasPay !== nowPay) {
    return nowPay ? { ...PAY_DEFAULTS } : { ...BOOKSTORE_DEFAULTS };
  }
  return {
    ...current,
    landingOptionPay: nowPay && current.landingOptionPay,
  };
};

/**
 * Decides what a vendor multi-select change means for the form.
 *
 * Student Pay vs bookstore is enforced in the dropdown (`vendorOptionsForSelection`): the
 * other side is disabled, matching Purchase Type. This still rejects a mixed payload if one
 * arrives, so codes are never silently queued for revocation.
 *
 * `acknowledgedRevokeVendors` are removals already confirmed in this edit. The pending list
 * still includes them so save revokes their codes, but the warning names only vendors this
 * change newly dropped.
 */
export const resolveVendorSelection = (params: {
  previousCodes: string[];
  nextCodes: string[];
  savedVendors: string[];
  inventoryByVendor: VendorInventoryMap;
  landingOptions: LandingOptions;
  acknowledgedRevokeVendors?: string[];
}): VendorSelectionResult => {
  const next = params.nextCodes || [];
  const hasStudentPay = next.indexOf(STUDENT_PAY) >= 0;
  const hasBookstore = next.some(isBookstoreVendor);

  if (hasStudentPay && hasBookstore) {
    const previousHadStudentPay =
      (params.previousCodes || []).indexOf(STUDENT_PAY) >= 0;
    return {
      blocked: true,
      message: previousHadStudentPay
        ? "Remove Student Pay before selecting bookstore vendors."
        : "Remove bookstore vendors before selecting Student Pay.",
    };
  }

  const pending = pendingRevokeVendors(
    params.savedVendors,
    next,
    params.inventoryByVendor
  );
  const acknowledged = params.acknowledgedRevokeVendors || [];
  const newlyPending = pending.filter((code) => acknowledged.indexOf(code) < 0);

  return {
    blocked: false,
    pendingRevokeVendors: pending,
    warning: newlyPending.length > 0 ? revokeWarning(newlyPending) : "",
    landingOptions: reconcileLandingOptions(
      params.landingOptions,
      params.previousCodes,
      next
    ),
  };
};

export type GenerateValidation = {
  vendorError: string;
  quantityError: string;
  valid: boolean;
};

/** Mirrors the server's own limits so the admin sees the problem before the round trip. */
export const validateGenerateRequest = (
  vendorKey: string | undefined | null,
  rawQuantity: unknown
): GenerateValidation => {
  const quantity = Number(rawQuantity);
  const vendorError = vendorKey ? "" : "The vendor field is required.";
  const quantityError =
    rawQuantity === ""
      || !Number.isInteger(quantity)
      || quantity < MIN_GENERATE_QUANTITY
      || quantity > MAX_GENERATE_QUANTITY
      ? `Enter a valid number from ${MIN_GENERATE_QUANTITY} to ${MAX_GENERATE_QUANTITY}`
      : "";
  return {
    vendorError,
    quantityError,
    valid: !vendorError && !quantityError,
  };
};

/** Max digits the Quantity field accepts (1000 is the largest allowed value). */
export const MAX_GENERATE_QUANTITY_DIGITS = String(MAX_GENERATE_QUANTITY).length;

/** Keystroke filter for Quantity: digits only, at most {@link MAX_GENERATE_QUANTITY_DIGITS}. */
export const isAcceptableQuantityInput = (raw: string): boolean =>
  new RegExp(`^\\d{0,${MAX_GENERATE_QUANTITY_DIGITS}}$`).test(raw);

/** Keys a whole-number field must not accept (`type="number"` allows them by default). */
const NON_INTEGER_KEYS = [".", ",", "e", "E", "+", "-"];

/** `onKeyDown` for whole-number inputs: blocks decimals, exponents and signs. */
export const preventNonIntegerKey = (event: { key: string; preventDefault: () => void }) => {
  if (NON_INTEGER_KEYS.includes(event.key)) {
    event.preventDefault();
  }
};

/**
 * Message to show for a failed API call: the server's text/`detail`/`message`/`title`, else
 * `fallback`. File downloads use `responseType: "blob"`, so their JSON error arrives as a Blob
 * and is read first.
 */
export const apiErrorMessage = async (err: any, fallback: string): Promise<string> => {
  let data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = text;
      }
    } catch (e) {
      data = null;
    }
  }
  return (
    (typeof data === "string" && data) ||
    data?.detail ||
    data?.message ||
    data?.title ||
    fallback
  );
};

/** Safe path segment for a download name. */
const toFilenamePart = (raw: string | undefined | null): string =>
  (raw || "")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * `{ClassName}-{Vendor...}.xls`, e.g. `Spring-Leadership-VitalSource-Follett.xls`.
 * Without vendors (download all) it is `{ClassName}-access-codes.xls`.
 */
export const codesFilename = (
  className: string | undefined | null,
  vendorKey?: string | string[] | null
): string => {
  const namePart = toFilenamePart(className) || "class";
  const keys = Array.isArray(vendorKey) ? vendorKey : vendorKey ? [vendorKey] : [];
  const vendorPart = keys
    .map((key) => toFilenamePart(vendorLabel(key)))
    .filter(Boolean)
    .join("-");
  return `${namePart}-${vendorPart || "access-codes"}.xls`;
};
